// Traduz qualquer erro (banco, API, login, rede) numa frase para o usuário.
// Os erros de regra do banco já vêm como "CODIGO: frase em português".

const UNICOS = {
  veiculos_placa_uk: 'Já existe um veículo com essa placa.',
  manutencao_itens_codigo_uk: 'Este item já está no plano deste caminhão.',
  colaboradores_celular_uk: 'Já existe um colaborador com esse celular.',
  empresas_documento_unico: 'Este CPF/CNPJ já tem uma conta no HSA Frota.',
  categorias_nome_empresa: 'Já existe uma categoria com esse nome.',
  membros_um_titular: 'A conta já tem um titular.',
};

const SOBREPOSICAO = {
  regras_sem_sobreposicao:
    'Já existe uma regra desse tipo valendo nesse período. Encerre a regra atual ou comece a nova depois do fim dela.',
  licencas_sem_sobreposicao: 'Já existe uma licença dessa placa nesse período.',
};

const CHECAGENS = {
  veiculos_placa_check: 'Placa inválida. Use o formato ABC1D23 ou ABC1234.',
  manutencao_itens_intervalo: 'Informe de quantos em quantos km ou meses o item vence.',
  manutencao_itens_nome_check: 'O nome do item precisa ter de 2 a 80 letras.',
  manutencao_itens_intervalo_km_check: 'O intervalo em km vai de 100 a 2.000.000.',
  manutencao_itens_intervalo_meses_check: 'O intervalo em meses vai de 1 a 120.',
  veiculos_renavam_check: 'O RENAVAM tem de 9 a 11 números.',
  veiculos_ano_check: 'Ano inválido.',
  veiculos_hodometro_inicial_check: 'O hodômetro não pode ser negativo.',
  veiculos_implemento_acoplado: 'A carreta precisa estar acoplada a um caminhão.',
  colaboradores_cpf_check: 'CPF inválido.',
  colaboradores_celular_check: 'Celular inválido. Informe DDD + número.',
  colaboradores_nome_check: 'Informe o nome (pelo menos 2 letras).',
  clientes_documento_check: 'CPF/CNPJ inválido.',
  clientes_nome_check: 'Informe o nome (pelo menos 2 letras).',
  lancamentos_sem_nota_motivo_check: 'Escreva por que não tem nota (de 2 a 120 letras).',
  clientes_uf_check: 'UF inválida.',
  clientes_prazo_saldo_dias_check: 'O prazo do saldo vai de 0 a 365 dias.',
  empresas_celular_check: 'Celular inválido. Informe DDD + número.',
  empresas_email_check: 'E-mail inválido.',
  empresas_uf_check: 'UF inválida.',
  empresas_nome_check: 'Informe o nome (pelo menos 2 letras).',
  regras_campos_por_componente: 'Preencha os campos pedidos para esse tipo de remuneração.',
  regras_vigencia: 'A data final precisa ser igual ou depois da inicial.',
  regras_remuneracao_percentual_check: 'O percentual vai de 0,01% a 100%.',
  regras_remuneracao_valor_check: 'O valor não pode ser negativo.',
  regras_remuneracao_faixa_ate_check: 'O limite do frete precisa ser maior que zero.',
  viagens_datas: 'A chegada não pode ser antes da saída.',
  viagens_km: 'O km da chegada não pode ser menor que o da saída.',
  viagens_encerrada_tem_chegada: 'Para encerrar a viagem, informe a data de chegada.',
  fretes_valor_frete_check: 'O valor do frete não pode ser negativo.',
  fretes_agenciamento: 'Confira quem agenciou a carga e o valor do agenciamento.',
  fretes_descontos: 'Pedágio e descontos passam do valor do frete.',
  fretes_adiantamento: 'O adiantamento passa do frete líquido mais a estadia.',
  fretes_datas: 'A descarga não pode ser antes do carregamento.',
  fretes_pedagio: 'Valor de pedágio só vale com o pedágio "embutido no frete".',
  fretes_comprovante: 'Informe quando o comprovante foi postado.',
  lancamentos_valor_check: 'O valor precisa ser maior que zero.',
  lancamentos_assinatura: 'A assinatura não pôde ser guardada. Assine de novo.',
};

export function codigoDoErro(erro) {
  const m = /^([A-Z][A-Z_]+):/.exec(erro?.message ?? '');
  return m ? m[1] : erro?.code ?? null;
}

export function mensagemDeErro(erro) {
  if (!erro) return '';
  const texto = String(erro.message ?? erro);

  // Regra do banco: "SEM_LICENCA: o caminhão ... não tinha licença em ..."
  const regra = /^([A-Z][A-Z_]+):\s*(.+)$/s.exec(texto);
  if (regra) return maiuscula(regra[2].trim());

  // O armazenamento do aparelho travou (falha conhecida do Safari)
  if (ehErroDoArmazenamento(erro)) {
    return 'O navegador deste aparelho travou o armazenamento do app. Feche o navegador por completo e abra o app de novo. '
      + 'No iPhone: deslize de baixo para cima e pare no meio da tela; depois, empurre o Safari para cima e abra o app de novo.';
  }

  // Sem internet, ou o servidor não respondeu direito (o navegador mostra
  // os dois do mesmo jeito; só quando o aparelho sabe que está sem rede dá
  // para dizer que é a internet).
  if (/failed to fetch|networkerror|load failed|fetch failed|network request failed/i.test(texto) || erro.name === 'AuthRetryableFetchError') {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return 'Sem conexão com a internet. Confira o sinal e tente de novo.';
    }
    return 'Não deu para falar com o servidor agora. Se a sua internet estiver funcionando, tente de novo em alguns minutos.';
  }

  // Login (Supabase Auth)
  const codigo = erro.code ?? '';
  if (codigo === 'otp_expired' || /token has expired or is invalid|otp.*(expired|invalid)/i.test(texto)) {
    return 'Código não aceito: está errado, venceu ou já foi usado. Use o do e-mail mais novo ou peça outro código.';
  }
  const espera = /after (\d+) seconds?/i.exec(texto);
  if (espera) return `Espere ${espera[1]} segundos para pedir outro código.`;
  if (codigo === 'over_email_send_rate_limit' || /email rate limit/i.test(texto)) {
    return 'Muitos pedidos de código seguidos. Espere alguns minutos e tente de novo.';
  }
  if (codigo === 'over_request_rate_limit' || erro.status === 429) {
    return 'Muitas tentativas seguidas. Espere um pouco e tente de novo.';
  }
  if (codigo === 'email_address_not_authorized' || /not authorized/i.test(texto)) {
    return 'Por enquanto o envio de código só funciona para o e-mail do administrador do sistema.';
  }
  if (codigo === 'email_address_invalid' || /invalid.*email|email.*invalid|unable to validate email/i.test(texto)) {
    return 'E-mail inválido. Confira o que foi digitado.';
  }
  if (codigo === 'signup_disabled') return 'Novos cadastros estão fechados no momento.';

  // Nenhuma linha gravada (conta passou a só consulta ou o registro sumiu)
  if (codigo === 'PGRST116') {
    return 'Não foi possível salvar: a conta está só para consulta ou o registro não existe mais. Atualize a tela.';
  }

  // Sessão vencida
  if (codigo === 'PGRST301' || codigo === 'PGRST303' || /jwt expired/i.test(texto)) {
    return 'Sua sessão expirou. Entre de novo.';
  }

  // Banco: restrições
  const restricao = /constraint "([^"]+)"/.exec(texto)?.[1];
  if (codigo === '23505') return UNICOS[restricao] ?? 'Já existe um cadastro igual a este.';
  if (codigo === '23P01') return SOBREPOSICAO[restricao] ?? 'Já existe um registro nesse mesmo período.';
  if (codigo === '23514') return CHECAGENS[restricao] ?? 'Algum campo está com um valor que não é aceito.';
  if (codigo === '23503') return 'O item escolhido não existe mais. Atualize a tela e tente de novo.';
  if (codigo === '23502') return 'Falta preencher um campo obrigatório.';
  if (codigo === '22P02' || codigo === '22007' || codigo === '22008') return 'Algum campo está num formato que não é aceito.';
  if (codigo === '42501' || /row-level security|permission denied/i.test(texto)) {
    return 'Sem permissão para gravar. Se o teste grátis acabou ou a assinatura está em aberto, a conta fica só para consulta.';
  }

  return `Não deu certo: ${texto}`;
}

function maiuscula(frase) {
  return frase.charAt(0).toUpperCase() + frase.slice(1);
}

// Uma linha curta com o erro técnico (nome, código, HTTP e a mensagem), em
// letra pequena embaixo da mensagem: no print mandado pelo WhatsApp, dá para
// saber se foi a internet, o servidor ou o aparelho.
export function detalheDoErro(erro) {
  if (erro == null) return 'sem descrição';
  if (typeof erro !== 'object') return String(erro).slice(0, 200);
  const nome = erro.name && erro.name !== 'Error' ? erro.name : null;
  const codigo = erro.code && erro.code !== erro.name ? String(erro.code) : null;
  const http = erro.status ? `HTTP ${erro.status}` : null;
  const partes = [nome, codigo, http].filter(Boolean).join(' · ');
  const texto = String(erro.message ?? '').trim();
  return [partes, texto].filter(Boolean).join(': ').slice(0, 200) || 'sem descrição';
}

// Falha de rede: sem sinal, servidor fora do ar ou pedido que passou do prazo.
export function ehFalhaDeRede(erro) {
  if (!erro) return false;
  if (erro.name === 'AuthRetryableFetchError' || erro.name === 'AbortError') return true;
  return /failed to fetch|networkerror|load failed|fetch failed|network request failed|err_internet_disconnected|aborterror|the operation was aborted|timeout/i
    .test(String(erro.message ?? erro));
}

// O que é da pessoa ou do momento, e não defeito do app: campo inválido,
// regra do banco, código de entrada errado, sessão vencida, cadastro
// repetido que o app já explica, sem sinal. Não vai para o registro de erros.
export function ehErroEsperado(erro) {
  if (erro == null || typeof erro !== 'object') return true;
  const texto = String(erro.message ?? '');
  const codigo = String(erro.code ?? '');
  if (/^[A-Z][A-Z_]+:/.test(texto)) return true;
  if (ehFalhaDeRede(erro) && typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  if (['otp_expired', 'over_email_send_rate_limit', 'over_request_rate_limit', 'email_address_invalid'].includes(codigo)) return true;
  if (/token has expired or is invalid|after \d+ seconds?|email rate limit|unable to validate email/i.test(texto)) return true;
  if (erro.status === 429) return true;
  if (codigo === 'PGRST301' || codigo === 'PGRST303' || /jwt expired/i.test(texto)) return true;
  const restricao = /constraint "([^"]+)"/.exec(texto)?.[1] ?? '';
  if (codigo === '23505' && Object.hasOwn(UNICOS, restricao)) return true;
  if (codigo === '23P01' && Object.hasOwn(SOBREPOSICAO, restricao)) return true;
  if (codigo === '23514' && Object.hasOwn(CHECAGENS, restricao)) return true;
  return false;
}

// O armazenamento do aparelho (IndexedDB) falhou por dentro. Acontece no
// Safari do iPhone de vez em quando, numa falha conhecida do WebKit ("Unable to
// open cursor", "Connection to Indexed Database server lost"…): fechar o
// Safari por completo costuma resolver; recarregar a página, nem sempre.
export function ehErroDoArmazenamento(erro) {
  if (!erro || typeof erro !== 'object') return false;
  const texto = String(erro.message ?? '');
  return erro.name === 'UnknownError'
    || /indexed ?database|unable to open cursor|cannot open cursor|error looking up record|attempt to get a record/i.test(texto);
}
